import * as THREE from 'three';
import { HUMPBACK, inHawaiiShare, type WhaleCounts } from '../sim/whales';
import type { Sprite } from '../ui/data/spriteTypes';
import { WHALE_SPRITES } from '../ui/data/whaleSprites';
import { FishBatch, facesRight, fishAtlas, nearFade, type FishDraw } from './fishBatch';
import { tapNearness } from './pick';
import { hash3 } from './fishSwim';

/**
 * The humpback whales in the exhibit (M99, the brief's Phase 6: "drawn in the aquarium in season"): the Hawaii
 * population's whales in Hawaii's waters on the day, as the simulation counts them (M101, `sim/whales.ts`), a
 * sprite for `WHALE_DRAW.whalesPerSprite` of them, and as many calves among them by the same; each cruising slowly
 * back and forth near the surface within the 183 m contour, where almost all of Hawaii's whales are found
 * ("almost exclusively within the 100-fathom (183-m) contours surrounding the main islands", Herman 1979, *Pacific
 * Science* 33, p. 2), a calf at its mother's side; fading in as the whales come in the winter and out as they leave.
 * What they stand for is Hawaii's: Maui Nui's share of them no source read gives as a number (M101's open item).
 * Drawn in one batch (`fishBatch.ts`), seen by the fish's light; the observation hour moves nothing of theirs, as no
 * source read gives their day. The arithmetic is pure and tested in node (`tests/whales.test.ts`).
 */

/**
 * How the whales are drawn (each a rendering choice, said so):
 *
 * - `whalesPerSprite`: a sprite for 1,000 whales (JUDGED: about ten at the peak, none in the summer).
 * - `lengthUnits`: a whale drawn 2.4 scene units long, about eight times a bullethead drawn (JUDGED: at the fish's
 *   own scale a 12.37 m whale would be 18 units, over half the tank; the exhibit is not to scale).
 * - `calfShare`: its calf drawn 5.35 m to its 12.37 m, the means NOAA's drones measured off Maui Nui of the calves and
 *   of the juveniles and adults (van Aswegen 2025, Table 4.1, `scripts/whaleSprites.ts`); by its sprite's pixels
 *   against the whale's until the whale became the user's pictures (M138).
 * - `contourM`: Herman 1979's 183 m contour, the deepest floor a whale is drawn over.
 * - `depthM`: each whale's cruising depth between 8 and 20 m, its sprite whole under the surface (JUDGED: near the
 *   surface, where they are seen; how deep they swim here is unread).
 * - `lengthsPerS`: an eighth of its length a second on average (JUDGED, near the fastest crossing measured, 4,830 km
 *   in 36 days, 1.55 m/s, an eighth of 12.37 m a second; Gabriele et al. 1996, as Bettridge et al. 2015 give it,
 *   p. 7), slowing to turn at each end of its lane.
 * - `fadeS`: seconds a whale takes to fade in or out as the whales come and go (JUDGED).
 * - `bobUnits`, `bobS`: a slow rise and fall as it swims (JUDGED).
 * - `calfAhead`, `calfUp`: the calf beside its mother, toward her head and a little above, whale lengths (JUDGED),
 *   above her body's middle: her sprite's box holds her flipper's downstroke below it (M138), so her body stands
 *   above the box's middle (`bodyUp`).
 * - `strokeS`: seconds each of the whale's two pictures is shown before the other, its flipper along its side then
 *   swept down (the user's word, M138: "have its image slowly alternate between the two images every 3 seconds to
 *   make it look like it's swimming"), none ever shorter (the user's word, M140: "the upwards fins frame should also
 *   last for 3 seconds"), each whale's stroke begun at its own random point (the user's word, M141: "for groups of
 *   whales, have their animation start time be random compared to other whales", `strokeStart`): from when it comes
 *   into the exhibit, its first picture, either of the two, held its 3 seconds and a random share of 3 more, so the
 *   whales of a pod swap at any point of the 6-second stroke against one another and none is cut short (M138 began
 *   each at its own point and cut its first picture short, MEASURED 0.50 to 3.00 s; M140 begun together, they swapped
 *   as one).
 */
export const WHALE_DRAW = {
  whalesPerSprite: 1000,
  lengthUnits: 2.4,
  contourM: 183,
  depthM: [8, 20],
  lengthsPerS: 0.125,
  fadeS: 3,
  bobUnits: 0.06,
  bobS: 23,
  calfAhead: 0.2,
  calfUp: 0.08,
  calfShare: 5.35 / 12.37,
  strokeS: 3,
} as const;

/**
 * Whale `k`'s stroke's start (M141, the user's word): the picture it shows first and how much longer than 3 seconds
 * it holds it, each from its own seeded draw (`hash3`, as its lane's are), so it begins the same each time it comes.
 */
export function strokeStart(k: number): { first: 0 | 1; holdS: number } {
  return { first: hash3(k, 0, 5) < 0.5 ? 0 : 1, holdS: WHALE_DRAW.strokeS * hash3(k, 0, 6) };
}

/** Which of whale `k`'s pictures is shown `seconds` after it came: its first, held 3 seconds and its extra, then each in turn for 3. */
export function strokeFrame(seconds: number, start: { first: 0 | 1; holdS: number }): 0 | 1 {
  return seconds < start.holdS ? start.first : (((start.first + strokeOf(seconds - start.holdS)) % 2) as 0 | 1);
}

/** Which of the whale's two pictures is shown `seconds` into its stroke, its first for 3 seconds, then the other. */
export function strokeOf(seconds: number): 0 | 1 {
  // Folded to 0 or 1 whatever the sign: the first frame's clock can fall a few milliseconds below 0 (M138's review).
  return (((Math.floor(seconds / WHALE_DRAW.strokeS) % 2) + 2) % 2) as 0 | 1;
}

/** Sprites for the whales in Hawaii's waters and the calves among them, a calf only beside a whale. */
export function whaleSpritesOf(inHawaii: number, calvesInHawaii: number, perSprite: number = WHALE_DRAW.whalesPerSprite): { whales: number; calves: number } {
  const whales = Math.round(Math.max(0, inHawaii - calvesInHawaii) / perSprite);
  return { whales, calves: Math.min(whales, Math.round(Math.max(0, calvesInHawaii) / perSprite)) };
}

/**
 * The day's sprites from the simulation's counts (M101's `whaleCounts`): the whales in Hawaii's waters, and the calves
 * there at the population's share there (a reading: the model counts the whales there, not the calves apart).
 */
export function humpbackSprites(c: WhaleCounts, epochDay: number): { whales: number; calves: number; calvesInHawaii: number } {
  const calvesInHawaii = c.calves * inHawaiiShare(HUMPBACK, epochDay);
  return { ...whaleSpritesOf(c.inHawaii, calvesInHawaii), calvesInHawaii };
}

/** A whale's lane: its front-to-back place, the ends it turns at, its middle's height, and its phases. */
export interface WhaleLane {
  z: number;
  x0: number;
  x1: number;
  y: number;
  phase: number;
  bob: number;
}

/** The tank as the lanes are found in it: its x and z spans, its floor's height and a real depth's height, a floor height's real depth. */
export interface WhaleWater {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
  floorY: (x: number, z: number) => number;
  depthY: (m: number) => number;
  depthOf: (y: number) => number;
}

/** Steps across the tank the lanes are searched in, and tries for each lane (JUDGED). */
const LANE_STEPS = 320;
const LANE_TRIES = 24;
/** The least room left under a whale's sprite above the floor, scene units (JUDGED). */
const FLOOR_GAP = 0.1;

/**
 * Whale `k`'s lane, the same for it however many are drawn: a place front to back and a cruising depth by its
 * seed, and the longest run across the tank where the floor lies within the contour and under its sprite by
 * `FLOOR_GAP`, its ends drawn in by half its length so it turns within it; tried again elsewhere where that run is
 * shorter than three lengths, null where no try finds one.
 */
export function whaleLane(k: number, water: WhaleWater, heightUnits: number, L: number = WHALE_DRAW.lengthUnits): WhaleLane | null {
  for (let t = 0; t < LANE_TRIES; t++) {
    const z = water.z0 + hash3(k, t, 1) * (water.z1 - water.z0);
    const d = WHALE_DRAW.depthM[0] + hash3(k, t, 2) * (WHALE_DRAW.depthM[1] - WHALE_DRAW.depthM[0]);
    const y = water.depthY(d);
    let best: [number, number] = [0, -1];
    let from = -1;
    for (let i = 0; i <= LANE_STEPS; i++) {
      const x = water.x0 + (i / LANE_STEPS) * (water.x1 - water.x0);
      const floor = water.floorY(x, z);
      const ok = floor < y - heightUnits / 2 - FLOOR_GAP && water.depthOf(floor) <= WHALE_DRAW.contourM;
      if (ok && from < 0) from = i;
      if ((!ok || i === LANE_STEPS) && from >= 0) {
        const to = ok ? i : i - 1;
        if (to - from > best[1] - best[0]) best = [from, to];
        from = -1;
      }
    }
    const at = (i: number): number => water.x0 + (i / LANE_STEPS) * (water.x1 - water.x0);
    const [x0, x1] = [at(best[0]) + L / 2, at(best[1]) - L / 2];
    if (x1 - x0 >= 3 * L) return { z, x0, x1, y, phase: hash3(k, t, 3), bob: hash3(k, t, 4) };
  }
  return null;
}

/** A whale `L` long on its lane `seconds` into its swim: its middle, and whether it faces right (its sprite faces left). */
export function whaleAt(lane: WhaleLane, seconds: number, L: number = WHALE_DRAW.lengthUnits): { x: number; y: number; right: boolean } {
  const span = lane.x1 - lane.x0;
  // Back and forth at the mean pace, slowing to turn at each end: a cosine's way along the lane.
  const period = (2 * span) / (WHALE_DRAW.lengthsPerS * L);
  const u = (((seconds / period + lane.phase) % 1) + 1) % 1;
  const along = (1 - Math.cos(2 * Math.PI * u)) / 2;
  const bob = WHALE_DRAW.bobUnits * (L / WHALE_DRAW.lengthUnits) * Math.sin(2 * Math.PI * (seconds / WHALE_DRAW.bobS + lane.bob));
  return { x: lane.x0 + span * along, y: lane.y + bob, right: Math.sin(2 * Math.PI * u) > 0 };
}

/** A whale drawn: which, whether a calf, its middle, its sprite's size, scene units, and its opacity. */
export interface WhaleDrawn {
  index: number;
  calf: boolean;
  middle: THREE.Vector3;
  width: number;
  height: number;
  alpha: number;
}

/** The exhibit's whales: their lanes, their sprites' count as the day has them, their swim and their fading. */
export class ExhibitWhales {
  readonly group = new THREE.Group();
  private readonly batch: FishBatch;
  private readonly rects: Map<Sprite, [number, number, number, number]>;
  private readonly lanes: (WhaleLane | null)[] = [];
  private readonly height: { whale: number; calf: number };
  private readonly calfLength: number;
  /** How far the whale's body's middle stands above her sprite's box's, scene units. */
  private readonly bodyUp: number;
  private want = { whales: 0, calves: 0 };
  private alphas: number[] = [];
  private calfAlphas: number[] = [];
  /** Each whale's seconds since it came into the exhibit, its stroke's clock (`strokeOf`). */
  private strokes: number[] = [];
  /**
   * Each whale's middle across the exhibit as last drawn, the way it last moved along its lane (+1 toward the world's
   * +x), and the way it faced on the screen, so it faces the way it is seen to move (M142).
   */
  private lastX: number[] = [];
  private ways: number[] = [];
  private facing: boolean[] = [];
  /** The camera they are seen by (`face`), null until one is given: the world's +x the screen's right. */
  private camera: THREE.Camera | null = null;
  private seconds = 0;
  private shown: WhaleDrawn[] = [];

  /**
   * @param lengthUnits a whale's length, scene units: the whole tank's `WHALE_DRAW.lengthUnits`, or a habitat scene's
   * true 12.37 m (M110)
   * @param depthTest whether it is hidden behind what stands nearer it (`FishBatch`'s)
   */
  constructor(private readonly water: WhaleWater, private readonly lengthUnits: number = WHALE_DRAW.lengthUnits, depthTest = false) {
    const { texture, rects } = fishAtlas([...WHALE_SPRITES.whale, WHALE_SPRITES.calf]);
    this.rects = rects;
    this.batch = new FishBatch(texture, depthTest);
    // Drawn over the fish, as a whale near the surface is nearer the glass than the reef behind it.
    this.batch.mesh.renderOrder = 5;
    this.group.add(this.batch.mesh);
    const L = lengthUnits;
    this.calfLength = L * WHALE_DRAW.calfShare;
    // The two frames the same size (`scripts/whaleSprites.ts`), so the first's measure is both's.
    const [whale] = WHALE_SPRITES.whale;
    this.height = { whale: (L * whale.height) / whale.width, calf: (this.calfLength * WHALE_SPRITES.calf.height) / WHALE_SPRITES.calf.width };
    // How far her body's middle (the first frame's drawn rows) stands above the box's (MEASURED: rows 0 to 58 of 78,
    // 0.061 of her length).
    const drawn = whale.rows.map((r) => /[^.]/.test(r));
    this.bodyUp = ((whale.height / 2 - (drawn.indexOf(true) + drawn.lastIndexOf(true) + 1) / 2) / whale.height) * this.height.whale;
  }

  private laneOf(k: number): WhaleLane | null {
    while (this.lanes.length <= k) this.lanes.push(whaleLane(this.lanes.length, this.water, this.height.whale, this.lengthUnits));
    return this.lanes[k] ?? null;
  }

  /** The day's sprites: those beyond them fade out, those new fade in, or are there at once when `atOnce` (a scene just opened, M110). */
  set(sprites: { whales: number; calves: number }, atOnce = false): void {
    this.want = sprites;
    for (let k = 0; k < sprites.whales; k++) this.laneOf(k);
    while (this.alphas.length < sprites.whales) this.alphas.push(atOnce ? 1 : 0);
    while (this.calfAlphas.length < sprites.whales) this.calfAlphas.push(atOnce && this.calfAlphas.length < sprites.calves ? 1 : 0);
  }

  /**
   * The swim's clock set so whale `k` is at its lane's middle, on its way across (M110: a habitat scene opened in the
   * season with a whale crossing its view, a rendering choice of when its animation starts; the whales are the day's
   * all the same).
   */
  meet(k: number): void {
    const lane = this.laneOf(k);
    if (!lane) return;
    const period = (2 * (lane.x1 - lane.x0)) / (WHALE_DRAW.lengthsPerS * this.lengthUnits);
    this.seconds = ((((0.25 - lane.phase) % 1) + 1) % 1) * period;
  }

  /** Moved on by `seconds` of the player's time, each faded toward being there or not; still in place when `calm`. */
  swim(seconds: number, calm = false): void {
    if (!calm) this.seconds = (this.seconds + seconds) % 86_400;
    const step = seconds / WHALE_DRAW.fadeS;
    const toward = (a: number, there: boolean): number => (there ? Math.min(1, a + step) : Math.max(0, a - step));
    this.alphas = this.alphas.map((a, k) => {
      const next = toward(a, k < this.want.whales && this.laneOf(k) !== null);
      // Its stroke begun as it comes (from none there, or there at once), run on while it is, still when calm.
      this.strokes[k] = a <= 0 || this.strokes[k] === undefined ? 0 : (this.strokes[k] as number) + (calm ? 0 : seconds);
      return next;
    });
    this.calfAlphas = this.calfAlphas.map((a, k) => toward(a, k < this.want.calves && this.laneOf(k) !== null));
    this.draw();
  }

  /**
   * Each turned the way `camera` sees it move across its screen (M142), by the camera as last placed, up to a frame
   * behind, as the fish are (`FishSchools.fit`).
   */
  face(camera: THREE.Camera): void {
    this.camera = camera;
    this.draw();
  }

  private draw(): void {
    const draws: FishDraw[] = [];
    const shown: WhaleDrawn[] = [];
    const whaleRects = WHALE_SPRITES.whale.map((f) => this.rects.get(f)) as [number, number, number, number][];
    const calfRect = this.rects.get(WHALE_SPRITES.calf) as [number, number, number, number];
    // Each whale, then its calf beside it, drawn after it so the calf is in front.
    this.alphas.forEach((alpha, k) => {
      const lane = this.lanes[k];
      if (!lane || alpha <= 0) return;
      const at = whaleAt(lane, this.seconds, this.lengthUnits);
      // Facing the way it last moved (the user's word, M140: "for all animals, the should face the direction they are
      // moving"), as the fish and the honu do; still, the way it last moved; first drawn, `whaleAt`'s (by its lane's way
      // it differed from its last move only at a turn, MEASURED 2 of 9,595 frames moved). That way as its camera sees
      // it (M142).
      const last = this.lastX[k];
      const way = last !== undefined && Math.abs(at.x - last) > 1e-9 ? Math.sign(at.x - last) : this.ways[k] ?? (at.right ? 1 : -1);
      const right = facesRight(this.camera, { x: at.x, y: at.y, z: lane.z }, { x: way, z: 0 }, this.facing[k] ?? at.right);
      this.lastX[k] = at.x;
      this.ways[k] = way;
      this.facing[k] = right;
      draws.push({ x: at.x, y: at.y - this.height.whale / 2, z: lane.z, width: this.lengthUnits, height: this.height.whale, rect: whaleRects[strokeFrame(this.strokes[k] ?? 0, strokeStart(k))] as [number, number, number, number], right, alpha });
      shown.push({ index: k, calf: false, middle: new THREE.Vector3(at.x, at.y, lane.z), width: this.lengthUnits, height: this.height.whale, alpha });
      // Never more than its mother's: a calf is wanted only where its mother is, and both fade at one pace.
      const calfAlpha = this.calfAlphas[k] ?? 0;
      if (calfAlpha <= 0) return;
      const L = this.lengthUnits;
      // Ahead of her the way she swims, whichever way she is seen.
      const [cx, cy] = [at.x + way * WHALE_DRAW.calfAhead * L, at.y + this.bodyUp + WHALE_DRAW.calfUp * L];
      // Ordered by her place (M157), so it stays drawn in front of her whichever way the camera looks.
      draws.push({ x: cx, y: cy - this.height.calf / 2, z: lane.z, width: this.calfLength, height: this.height.calf, rect: calfRect, right, alpha: calfAlpha, sortAt: { x: at.x, y: at.y - this.height.whale / 2, z: lane.z } });
      shown.push({ index: k, calf: true, middle: new THREE.Vector3(cx, cy, lane.z), width: this.calfLength, height: this.height.calf, alpha: calfAlpha });
    });
    this.batch.set(draws);
    this.shown = shown;
  }

  /** The light they are seen by, each channel 0 to 1, as the fish's (M96). */
  setLight(r: number, g: number, b: number): void {
    this.batch.setLight(r, g, b);
  }

  /** The water they are seen through, the floor's fog (`FishBatch.fogUniforms`). */
  fogUniforms(): ReturnType<FishBatch['fogUniforms']> {
    return this.batch.fogUniforms();
  }

  /**
   * The whale under a point on the screen: within its sprite's box on screen, or `withinPx` of its middle where the
   * box is smaller (M99's review: a circle of half its length reached a sprite and a half above and below it, and
   * took the taps of the fish there), the nearest; its calf's mother's for a calf; -1 for none.
   */
  pick(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): number {
    const at = new THREE.Vector3();
    const end = new THREE.Vector3();
    const top = new THREE.Vector3();
    let best = Infinity;
    let found = -1;
    for (const w of this.shown) {
      if (w.alpha < 0.5) continue;
      at.copy(w.middle).project(camera);
      if (at.z < -1 || at.z > 1) continue;
      end.copy(w.middle).setX(w.middle.x + (w.calf ? this.calfLength : this.lengthUnits) / 2).project(camera);
      top.copy(w.middle).setY(w.middle.y + (w.calf ? this.height.calf : this.height.whale) / 2).project(camera);
      // None faded out by its nearness to the camera (`NEAR_FADE`; M105's review: an unseen whale took the taps).
      if (nearFade(Math.abs(end.x - at.x) * widthPx, widthPx) < 0.5) continue;
      const [dx, dy] = [(Math.abs(at.x - x) * widthPx) / 2, (Math.abs(at.y - y) * heightPx) / 2];
      const inside = dx <= Math.max(withinPx, (Math.abs(end.x - at.x) * widthPx) / 2) && dy <= Math.max(withinPx, (Math.abs(top.y - at.y) * heightPx) / 2);
      if (inside && Math.hypot(dx, dy) < best) {
        best = Math.hypot(dx, dy);
        found = w.index;
      }
    }
    return found;
  }

  /** Whale `index` as drawn now (its calf beside it, if one is), or null where it is not drawn. */
  /** How near a tap is to a whale or its calf as drawn (`tapNearness`), the nearer of the two; Infinity for neither. */
  tapNearness(camera: THREE.Camera, index: number, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): number {
    return Math.min(Infinity, ...this.shown.filter((w) => w.index === index).map((w) => tapNearness({ middle: w.middle, width: w.calf ? this.calfLength : this.lengthUnits, height: w.calf ? this.height.calf : this.height.whale, alpha: w.alpha }, camera, x, y, widthPx, heightPx, withinPx)));
  }

  at(index: number): { whale: WhaleDrawn; calf: WhaleDrawn | null } | null {
    const whale = this.shown.find((w) => w.index === index && !w.calf);
    if (!whale) return null;
    return { whale, calf: this.shown.find((w) => w.index === index && w.calf) ?? null };
  }

  /** The sprites the day has them at, and those drawn now, for the card and the tests. */
  counts(): { whales: number; calves: number } {
    return { ...this.want };
  }

  drawn(): WhaleDrawn[] {
    return this.shown;
  }

  /** What the batch draws, for the tests. */
  draws(): FishDraw[] {
    return this.batch.drawn();
  }

  dispose(): void {
    this.batch.dispose();
  }
}
